/* Three gestures, deliberately distinct:
   - swipeStartFrom / detectHorizontalSwipe: TouchEvent, fixed-px threshold,
     fire-and-forget. Right for discrete flicks (tab strips).
   - HorizontalDrag: PointerEvent, width-relative + velocity, pointer capture,
     axis lock. Right for a card carousel you can push around.
   - SheetDrag: PointerEvent, peek/expand detent sheet on window-level move
     listeners. Right for a drag sheet pinned to a screen edge — the pull-down
     log shade and the pull-up online-players sheet share it.
   Pick the one that fits; don't add a fourth. */

export interface SwipeStart {
  x: number;
  y: number;
  t: number;
}

export interface SwipeOptions {
  minDx?: number;
  maxDtMs?: number;
}

export function swipeStartFrom(event: TouchEvent): SwipeStart | null {
  const t = event.touches[0];
  return t ? { x: t.clientX, y: t.clientY, t: Date.now() } : null;
}

// Returns -1 for a left swipe, 1 for right, or null if the gesture didn't
// clear the threshold (too slow, too short, or mostly vertical).
export function detectHorizontalSwipe(
  start: SwipeStart | null,
  event: TouchEvent,
  opts: SwipeOptions = {},
): -1 | 1 | null {
  if (!start) return null;
  const end = event.changedTouches[0];
  if (!end) return null;
  const minDx = opts.minDx ?? 40;
  const maxDtMs = opts.maxDtMs ?? 500;
  const dx = end.clientX - start.x;
  const dy = end.clientY - start.y;
  if (Date.now() - start.t > maxDtMs) return null;
  if (Math.abs(dy) > Math.abs(dx)) return null;
  if (Math.abs(dx) < minDx) return null;
  return dx < 0 ? -1 : 1;
}

/** Movement before the gesture commits to an axis. */
const AXIS_LOCK_PX = 8;
/** Commit distance, as a fraction of the dragged element's width... */
const DISTANCE_RATIO = 0.25;
/** ...or commit on a flick this fast, however short. */
const MIN_VELOCITY_PX_PER_MS = 0.4;

/**
 * Pointer-driven horizontal drag for card carousels. Captures the pointer, locks
 * to an axis on first movement, and releases to the page on vertical intent so
 * scrolling still works. Wire all four handlers; `end()` reports the direction.
 */
export class HorizontalDrag {
  private state: {
    pointerId: number;
    startX: number;
    startY: number;
    startT: number;
    dx: number;
    axis: 'x' | 'y' | null;
    target: HTMLElement;
    width: number;
  } | null = null;

  start(e: PointerEvent): void {
    // Let buttons and links have their own clicks.
    if ((e.target as HTMLElement).closest('button, a')) return;
    const target = e.currentTarget as HTMLElement | null;
    if (!target) return;

    this.state = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startT: performance.now(),
      dx: 0,
      axis: null,
      target,
      width: target.clientWidth || 1,
    };
    try { target.setPointerCapture(e.pointerId); } catch {}
  }

  move(e: PointerEvent): void {
    const s = this.state;
    if (!s || e.pointerId !== s.pointerId) return;

    const dx = e.clientX - s.startX;
    const dy = e.clientY - s.startY;

    if (s.axis === null) {
      if (Math.abs(dx) <= AXIS_LOCK_PX && Math.abs(dy) <= AXIS_LOCK_PX) return;
      s.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      // Vertical intent → hand the gesture back so the page can scroll.
      if (s.axis === 'y') { this.cancel(e); return; }
    }

    e.preventDefault();
    s.dx = dx;
  }

  // -1/1 for a committed left/right drag, null if it didn't clear the threshold.
  // 'tap' = pointer never moved past the axis lock: callers can rotate on a click
  // without a DOM `click`, which pointer capture retargets off the child.
  end(e: PointerEvent): -1 | 1 | 'tap' | null {
    const s = this.state;
    if (!s || e.pointerId !== s.pointerId) return null;

    const { dx, width, startT, axis } = s;
    this.release();
    if (axis === null) return 'tap';
    if (axis !== 'x') return null;

    const velocity = Math.abs(dx) / Math.max(performance.now() - startT, 1);
    if (Math.abs(dx) < width * DISTANCE_RATIO && velocity < MIN_VELOCITY_PX_PER_MS) return null;
    return dx < 0 ? -1 : 1;
  }

  cancel(e: PointerEvent): void {
    if (!this.state || e.pointerId !== this.state.pointerId) return;
    this.release();
  }

  private release(): void {
    if (this.state) {
      try { this.state.target.releasePointerCapture(this.state.pointerId); } catch {}
    }
    this.state = null;
  }
}

/** Movement before a sheet drag engages; a press that never clears this is a tap. */
const SHEET_SLOP_PX = 8;
/** Past this fraction of the detent gap, release settles to the far detent... */
const SHEET_SETTLE_RATIO = 1 / 3;
/** ...or on a flick at least this fast (px/ms), however short. */
const SHEET_FLICK_PX_PER_MS = 0.5;

const clamp01 = (n: number): number => Math.min(1, Math.max(0, n));

/**
 * The host owns the visuals; SheetDrag owns the gesture. `openDir` says which
 * drag direction opens ('down' = top-anchored shade, 'up' = bottom sheet); the
 * host maps a 0→1 progress to its own paint (height, or transform + scrim) and
 * animates the settle.
 */
export interface SheetDragHost {
  readonly openDir: 'up' | 'down';
  /** px between the collapsed and open detents, read once at grab start. */
  measureTravel(): number;
  /** Open state at grab start — the "stay" default when a release is indecisive. */
  isOpen(): boolean;
  /** Optional: a fresh press arrived; cancel any in-flight settle. */
  onStart?(): void;
  /** First move past the slop — disable the settle transition before painting. */
  onEngage(): void;
  /** Paint an in-flight drag: `progress` 0 = collapsed, 1 = open. */
  onDrag(progress: number, travel: number): void;
  /** Release decided: animate to the detent and record state. `engaged` is false
   *  for a tap, which each host resolves its own way (toggle, or defer to a button). */
  onSettle(open: boolean, engaged: boolean, travel: number): void;
  /** The gesture was taken away mid-flight — revert to the resting state rather
   *  than settling, since the release position never expressed an intent. */
  onCancel?(): void;
}

interface SheetDragState {
  pointerId: number;
  startY: number;
  startT: number;
  travel: number;
  base: number; // isOpen ? 1 : 0
  dy: number;
  engaged: boolean;
}

/**
 * Peek/expand detent sheet driven by a pointer drag. Owns the window-level
 * move/up/cancel listeners, so an in-flight drag paints inline styles without
 * ticking change detection; the slop-to-engage latch; and the
 * flick-beats-distance settle decision. Wire `start` to pointerdown on the grab
 * element and call `destroy` from the host's DestroyRef.
 */
export class SheetDrag {
  private state: SheetDragState | null = null;

  constructor(private readonly host: SheetDragHost) {}

  readonly start = (e: PointerEvent): void => {
    // A host can bind several grab elements; pressing a second one abandons the first.
    this.detach();
    this.host.onStart?.();
    this.state = {
      pointerId: e.pointerId,
      startY: e.clientY,
      startT: performance.now(),
      travel: this.host.measureTravel(),
      base: this.host.isOpen() ? 1 : 0,
      dy: 0,
      engaged: false,
    };
    // Window-level rather than pointer capture on the grab element: capture would
    // deliver the same events, but it also retargets a tap's click to the capture
    // element, stealing it from any button inside. Native listeners keep an
    // in-flight drag from ticking change detection.
    window.addEventListener('pointermove', this.move);
    window.addEventListener('pointerup', this.end);
    window.addEventListener('pointercancel', this.cancel);
  };

  private readonly move = (e: PointerEvent): void => {
    const s = this.state;
    if (!s || e.pointerId !== s.pointerId) return;
    // No gap between the detents means nothing to drag against — and dividing by
    // it would paint NaN.
    if (s.travel <= 0) return;
    s.dy = e.clientY - s.startY;
    if (!s.engaged) {
      if (Math.abs(s.dy) < SHEET_SLOP_PX) return;
      s.engaged = true;
      this.host.onEngage();
    }
    const dir = this.host.openDir === 'down' ? 1 : -1;
    this.host.onDrag(clamp01(s.base + (dir * s.dy) / s.travel), s.travel);
  };

  private readonly end = (e: PointerEvent): void => {
    const s = this.claim(e);
    if (!s) return;

    const dir = this.host.openDir === 'down' ? 1 : -1;
    const velocity = s.dy / Math.max(performance.now() - s.startT, 1);
    // A flick beats position; otherwise a large-enough drag flips, else stay put.
    let open = s.base === 1;
    if (Math.abs(s.dy) > s.travel * SHEET_SETTLE_RATIO || Math.abs(velocity) > SHEET_FLICK_PX_PER_MS) {
      open = dir * s.dy > 0;
    }
    this.host.onSettle(open, s.engaged, s.travel);
  };

  private readonly cancel = (e: PointerEvent): void => {
    if (!this.claim(e)) return;
    this.host.onCancel?.();
  };

  /** End the gesture and hand back its state, if this pointer owns it. */
  private claim(e: PointerEvent): SheetDragState | null {
    const s = this.state;
    if (!s || e.pointerId !== s.pointerId) return null;
    this.detach();
    this.state = null;
    return s;
  }

  private detach(): void {
    window.removeEventListener('pointermove', this.move);
    window.removeEventListener('pointerup', this.end);
    window.removeEventListener('pointercancel', this.cancel);
  }

  destroy(): void {
    this.detach();
    this.state = null;
  }
}
